import { RefObject, useEffect, useRef, useState } from 'react';

import type { EmbedDemoConfig } from '@/lib/api/features';
import { cn } from '@/lib/utils';

interface EmbedExamplesSectionProps {
  demos: EmbedDemoConfig[];
  /** Open the live iframe preview — only called for demos with a deployUrl. */
  onTryLive: (demo: EmbedDemoConfig) => void;
  /** Seed the prompt box from a demo's promptSeed. */
  onUseIdea: (demo: EmbedDemoConfig) => void;
}

/* Base render size for the live iframe — the app is laid out at its real design
 * resolution (1080p — what these signage apps are authored for) and scaled to
 * "cover" the 16:9 stage, so it renders at the right breakpoint and stays crisp. */
const IFRAME_W = 1920;
const IFRAME_H = 1080;

/**
 * A scaled-down LIVE render of a deployed OptiDev signage app. Laid out at
 * 1280×720 and transform-scaled to cover its container. Lazy-mounted via
 * IntersectionObserver so it only loads when near the viewport.
 */
function LiveScreen({ url }: { url: string }): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setScale(Math.max(w / IFRAME_W, h / IFRAME_H));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0 overflow-hidden bg-black">
      {visible && scale > 0 ? (
        <iframe
          src={url}
          title="Live OptiDev signage demo"
          tabIndex={-1}
          scrolling="no"
          aria-hidden="true"
          // pointer-events-none: clicks fall through to the activate button so
          // the whole stage opens the full preview instead of interacting here.
          className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
          style={{
            width: `${IFRAME_W}px`,
            height: `${IFRAME_H}px`,
            transform: `scale(${scale})`,
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          loading="lazy"
        />
      ) : (
        <ScreenSkeleton />
      )}
    </div>
  );
}

/**
 * Animated faux-signage screen for idea-only demos (no deployUrl) and for the
 * selector thumbnails without an image. A drifting brand wash + shimmering
 * layout blocks + a sweeping ticker — size-agnostic (all fractions) so it works
 * at both the large preview and the small card. Implies "your content here".
 */
function IdeaScreen(): JSX.Element {
  return (
    <div className="absolute inset-0 overflow-hidden bg-zinc-950">
      <div className="ex-drift absolute -inset-1/4 opacity-80" />
      <div className="absolute inset-0 flex flex-col gap-[3%] p-[5%]">
        <div className="flex items-center justify-between">
          <span className="ex-shimmer h-1.5 w-1/3 rounded-full" />
          <span className="ex-shimmer h-1.5 w-[12%] rounded-full" />
        </div>
        <span className="ex-shimmer h-[40%] w-full rounded-lg" />
        <div className="grid flex-1 grid-cols-3 gap-[3%]">
          <span className="ex-shimmer rounded-md" />
          <span className="ex-shimmer rounded-md" />
          <span className="ex-shimmer rounded-md" />
        </div>
        <div className="h-[8%] min-h-[6px] w-full overflow-hidden rounded-full bg-white/5">
          <div className="ex-ticker h-full w-1/2 rounded-full bg-gradient-to-r from-transparent via-purple-400/70 to-transparent" />
        </div>
      </div>
    </div>
  );
}

/** Quiet loading shimmer shown under a live iframe before it mounts/paints. */
function ScreenSkeleton(): JSX.Element {
  return (
    <div className="absolute inset-0 bg-zinc-950">
      <div className="ex-drift absolute -inset-1/4 opacity-50" />
    </div>
  );
}

/** Reveal-on-scroll: returns a ref + a `shown` flag that flips true (once) when
 *  the element scrolls into view. Drives the editorial entrance animations. */
function useReveal<T extends HTMLElement>(): { ref: RefObject<T>; shown: boolean } {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, shown };
}

/**
 * One example in the scroll sequence: a writeup (label, description, CTA) beside
 * its live preview. The two sides alternate per row (zig-zag). Live demos play
 * their real app inline; idea-only demos show the animated faux-screen. The CTA
 * opens the full live preview (deployUrl) or seeds the prompt (promptSeed).
 */
function DemoRow({
  demo,
  index,
  reversed,
  onTryLive,
  onUseIdea,
}: {
  demo: EmbedDemoConfig;
  index: number;
  reversed: boolean;
  onTryLive: (demo: EmbedDemoConfig) => void;
  onUseIdea: (demo: EmbedDemoConfig) => void;
}): JSX.Element {
  const isLive = !!demo.deployUrl;
  const canUseIdea = !!demo.promptSeed;
  const interactive = isLive || canUseIdea;

  const activate = (): void => {
    if (isLive) onTryLive(demo);
    else if (canUseIdea) onUseIdea(demo);
  };

  const { ref, shown } = useReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12"
    >
      {/* Preview — the large focus (~58% width), framed in a device bezel so it
          reads like a real display rather than a flat rectangle. Reveals on
          scroll for an editorial, unfolding feel. */}
      <div
        className={cn(
          'transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:!translate-y-0 motion-reduce:!opacity-100 motion-reduce:transition-none lg:col-span-7',
          reversed && 'lg:order-2',
          shown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        )}
      >
        <div className="rounded-[1.25rem] bg-gradient-to-b from-zinc-200 to-zinc-300 p-1 ring-1 ring-black/10 sm:p-1.5 dark:from-zinc-800 dark:to-zinc-950 dark:ring-white/10">
          <div className="group relative aspect-video w-full overflow-hidden rounded-xl bg-black ring-1 ring-black/60">
            {isLive ? (
              <LiveScreen url={demo.deployUrl as string} />
            ) : (
              <IdeaScreen />
            )}
            <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/5" />
            {interactive && (
              <button
                type="button"
                onClick={activate}
                aria-label={isLive ? `Open live demo: ${demo.label}` : `Use this idea: ${demo.label}`}
                className="absolute inset-0 cursor-pointer bg-black/0 transition-colors duration-300 hover:bg-black/5"
              />
            )}
          </div>
        </div>
      </div>

      {/* Writeup — compact, ~42% width. Reveals just after the preview (stagger). */}
      <div
        className={cn(
          'transition-all delay-150 duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:!translate-y-0 motion-reduce:!opacity-100 motion-reduce:transition-none lg:col-span-5',
          reversed && 'lg:order-1',
          shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
        )}
      >
        {/* Eyebrow — sequence index + a contextual live/idea tag for structure */}
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm font-semibold tabular-nums text-muted-foreground/40">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="h-px w-6 bg-border" />
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {isLive ? (
              <>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                Live preview
              </>
            ) : (
              'Start from an idea'
            )}
          </span>
        </div>
        <h3 className="text-xl font-semibold tracking-tight sm:text-2xl">
          {demo.label}
        </h3>
        {demo.description && (
          <p className="mt-2.5 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
            {demo.description}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {isLive && (
            <button
              type="button"
              onClick={() => onTryLive(demo)}
              className="inline-flex items-center gap-2 rounded-xl border border-primary bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/90 hover:bg-primary/90 hover:shadow-md"
            >
              Try it live
            </button>
          )}
          {canUseIdea && (
            <button
              type="button"
              onClick={() => onUseIdea(demo)}
              className={cn(
                'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md',
                isLive
                  ? 'border border-foreground/30 text-foreground hover:bg-foreground hover:text-background'
                  : 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
              )}
            >
              Use this prompt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * OptiDev signage examples — every demo shown in a vertical scroll sequence, one
 * after another, each as a writeup beside its live preview with the sides
 * alternating (zig-zag). Live demos play their real app inline; idea-only demos
 * show the animated faux-screen.
 */
export function EmbedExamplesSection({
  demos,
  onTryLive,
  onUseIdea,
}: EmbedExamplesSectionProps): JSX.Element {
  if (demos.length === 0) return <></>;

  return (
    <>
      <div>
        {demos.map((demo, i) => (
          <div
            key={demo.label}
            className={cn(
              'w-full',
              i % 2 === 0 && 'bg-black/[0.03] dark:bg-white/[0.028]',
            )}
          >
            <div className="mx-auto max-w-6xl px-5 py-24 sm:py-32">
              <DemoRow
                demo={demo}
                index={i}
                reversed={i % 2 === 1}
                onTryLive={onTryLive}
                onUseIdea={onUseIdea}
              />
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes exDrift {
          0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg); }
          50%  { transform: translate3d(4%, 3%, 0) rotate(8deg); }
          100% { transform: translate3d(-4%, -3%, 0) rotate(0deg); }
        }
        .ex-drift {
          background:
            radial-gradient(40% 50% at 25% 25%, rgba(168, 85, 247, 0.45), transparent 70%),
            radial-gradient(45% 55% at 80% 30%, rgba(99, 102, 241, 0.40), transparent 70%),
            radial-gradient(50% 60% at 55% 85%, rgba(217, 70, 239, 0.30), transparent 70%);
          animation: exDrift 14s ease-in-out infinite;
        }
        @keyframes exShimmer {
          0%   { background-position: -150% 0; }
          100% { background-position: 250% 0; }
        }
        .ex-shimmer {
          background-image: linear-gradient(
            100deg,
            rgba(255, 255, 255, 0.06) 30%,
            rgba(255, 255, 255, 0.18) 50%,
            rgba(255, 255, 255, 0.06) 70%
          );
          background-size: 200% 100%;
          animation: exShimmer 2.8s ease-in-out infinite;
        }
        @keyframes exTicker {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(220%); }
        }
        .ex-ticker { animation: exTicker 3.6s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .ex-drift, .ex-shimmer, .ex-ticker { animation: none; }
        }
      `}</style>
    </>
  );
}
